/**
 * useSearchHistory Hook
 *
 * Stores and retrieves the user's recently searched subcategories in localStorage.
 * Falls back gracefully on SSR (returns empty array server-side).
 */

'use client';

import { useState, useCallback } from 'react';

const STORAGE_KEY = 'hummii_search_history';
const MAX_HISTORY = 5;

export interface SearchHistoryEntry {
  id: string;
  slug: string;
  nameEn: string;
  nameFr: string;
  parentId: string | null;
}

function readHistory(): SearchHistoryEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SearchHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function writeHistory(entries: SearchHistoryEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Ignore storage quota errors
  }
}

export interface UseSearchHistoryReturn {
  history: SearchHistoryEntry[];
  addToHistory: (entry: SearchHistoryEntry) => void;
  clearHistory: () => void;
}

export function useSearchHistory(): UseSearchHistoryReturn {
  const [history, setHistory] = useState<SearchHistoryEntry[]>(readHistory);

  const addToHistory = useCallback((entry: SearchHistoryEntry) => {
    setHistory((prev) => {
      const deduped = prev.filter((h) => h.slug !== entry.slug);
      const next = [entry, ...deduped].slice(0, MAX_HISTORY);
      writeHistory(next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    writeHistory([]);
    setHistory([]);
  }, []);

  return { history, addToHistory, clearHistory };
}
